import * as styles from "./QueryExplanation.css";

import { useLingui } from "@lingui/react";

import type { FilterToken } from "../dsl/types";
import { commandPaletteLabels } from "../i18n/labels";
import { getResultTypeDefinition } from "../results/registry";

export type QueryExplanationProps = {
  tokens: FilterToken[];
  freeText: string;
  resultCount: number;
  totalCount?: number;
};

export function QueryExplanation({
  tokens,
  freeText,
  resultCount,
  totalCount,
}: QueryExplanationProps) {
  const { _ } = useLingui();

  const labelForToken = (token: FilterToken): string => {
    if ("type" === token.key) {
      const definition = getResultTypeDefinition(token.value);

      return definition ? _(definition.label) : token.value;
    }

    return token.value;
  };

  const groups: { key: string; labels: string[] }[] = [];

  for (const token of tokens) {
    const label = labelForToken(token);
    const group = groups.find((candidate) => candidate.key === token.key);

    if (group) {
      group.labels.push(label);
    } else {
      groups.push({ key: token.key, labels: [label] });
    }
  }

  const orWord = _(commandPaletteLabels.orConnector);
  const andWord = _(commandPaletteLabels.andConnector);

  const clauses = groups.map(
    (group) => `${group.key} = ${group.labels.join(` ${orWord} `)}`,
  );

  const trimmedFreeText = freeText.trim();

  if (trimmedFreeText) {
    clauses.push(
      _({
        ...commandPaletteLabels.textContains,
        values: { text: trimmedFreeText },
      }),
    );
  }

  if (0 === clauses.length) {
    return null;
  }

  return (
    <div className={styles.bar}>
      <span className={styles.clauses}>{clauses.join(` ${andWord} `)}</span>
      {undefined !== totalCount && (
        <span className={styles.count}>
          {_({
            ...commandPaletteLabels.resultsCount,
            values: { count: resultCount, total: totalCount },
          })}
        </span>
      )}
    </div>
  );
}
